<template>
  <div class="discounts">
    <div class="discounts__header">
      <div>Click to add or remove Alpine rewards to this order.</div>
      <div>Points: {{ appliedPoints }}<strong>/{{ pointBalance }}</strong> - Remaining: {{ pointBalance - appliedPoints
        }}
      </div>
    </div>
    <div v-if="loyaltyDiscounts.length == 0 && !loadingGetAvailableDiscountsStatus">
      <div class="discounts__no_items">
        No discounts available
      </div>
    </div>
    <div class="discounts__container">
      <div class="discounts__grid" :class="{ blur: isBlurred }">
        <div class="discounts__item" v-for="disc in loyaltyDiscounts" :key="disc.id"
          @click="!isBlurred && (isSelected(disc) ? removeDiscount(disc) : addDiscount(disc))"
          :class="{ 'discounts__item--selected': isSelected(disc) }">
          <div class="discounts__item__title">{{ disc.pageTitle }}</div>
          <div class="discounts__item__details">
            <div class="discounts__item__stacked"><span v-if="disc.disableStacking">Cannot be stacked.</span></div>
            <div class="discounts__item__points">{{ disc.pointsDeduction }} points</div>
          </div>
        </div>
      </div>
    </div>
    <div class="discounts__actions">
      <BaseBtn @click="close" :label="'Close'" :colorType="3" />
    </div>
  </div>
</template>

<script>

import BaseBtn from "@/components/Base/Button/Button.vue";
import Constants from "@/resources/Constants";
import { min } from "lodash";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "LoyaltyDiscounts",
  data() {
    return {
      isBlurred: false,
      Constants: Constants,
      pointBalance: 0,
      loyaltyDiscounts: [],
      selectedDiscounts: []
    };
  },
  props: {
    customer: {
      type: Object,
      required: true,
    }
  },
  components: {
    BaseBtn,
  },
  watch: {
    loadingGetAvailableDiscountsStatus: {
      handler(newVal) {
        this.isBlurred = newVal;
      },
      immediate: true,
    },
  },
  created: function () {
    this.getAvailableDiscounts({
      positiveCallback: (result) => {
        this.loyaltyDiscounts = result?.discounts ?? [];
        this.pointBalance = result?.pointBalance ?? 0;
      },
      negativeCallback: (error) => {
        console.log(error);
      },
      body: { customer: this.customer, order: this.posOrder },
    });

    this.setSelectedDiscounts(this.posOrder)
  },
  computed: {
    ...mapGetters({
      getPosOrder: "orderModule/posOrder",
      currentStore: "storeModule/currentStore",
      loadingGetAvailableDiscountsStatus: "loyaltyModule/loadingGetAvailableDiscountsStatus",
    }),
    posOrder() {
      return this.getPosOrder;
    },
    useDiscountRulesEngine() {
      return this.currentStore?.useDiscountRulesEngine || false;
    },
    appliedPoints() {
      return this.selectedDiscounts?.reduce((acc, curr) => acc + (curr?.alpineDiscount?.pointsDeduction ?? 0), 0) ?? 0;
    },
  },
  methods: {
    ...mapActions({
      setDiscountsPopUp: "posModule/setDiscountsPopUp",
      applyDiscountToOrder: "orderModule/applyDiscountToOrder",
      removeDiscountFromOrder: "orderModule/removeDiscountFromOrder",
      getAvailableDiscounts: "loyaltyModule/getAvailableDiscounts",
      getCouponById: "couponModule/getCouponById",
      getDiscountRuleById: "discountRuleModule/getDiscountRuleById",
      setMessage: "setMessage",
    }),
    isSelected(discount) {
      console.log(this.selectedDiscounts, discount);
      // In engine mode, check discountsApplied for alpine metadata entry
      // The tracking entry has alpineDiscount.couponId matching the ruleId (posDiscountID)
      if (this.useDiscountRulesEngine) {
        return this.selectedDiscounts?.some(disc =>
          disc.type === 'alpine-discount' &&
          disc.alpineDiscount?.couponId === discount.posDiscountID?.toString()
        ) || false;
      }
      // Legacy mode
      return this?.selectedDiscounts?.some(disc =>
        discount.id === disc.id ||
        discount.posDiscountID === disc.alpineDiscount?.couponId
      );
    },
    async addDiscount(discount) {
      const canAdd = this.validateAlpineDiscountsApplied(discount)
      if (!canAdd) return
      this.isBlurred = true;

      // Engine mode: look up DiscountRule by posDiscountID and send coupon-type payload
      if (this.useDiscountRulesEngine) {
        const ruleId = discount.posDiscountID;
        this.getDiscountRuleById({
          _id: ruleId,
          positiveCallback: async (rule) => {
            const alpineDiscount = { ...discount, couponId: rule._id.toString() };
            const discountPayload = {
              type: "coupon",
              discountId: rule._id,
              discountType: "coupon",
              code: rule.code,
              couponName: rule.name,
              alpineDiscount: alpineDiscount,
            };
            try {
              const order = await this.applyDiscountToOrder({
                discount: discountPayload,
                orderId: this.posOrder._id,
                order: this.posOrder,
              });
              this.setSelectedDiscounts(order);
              this.isBlurred = false;
            } catch (error) {
              this.isBlurred = false;
            }
          },
          negativeCallback: () => {
            this.isBlurred = false;
          }
        });
        return;
      }

      // Legacy mode: look up Coupon by posDiscountID
      const couponId = discount.posDiscountID;
      this.getCouponById({
        _id: couponId,
        positiveCallback: async (result) => {
          const couponDiscountType = (result?.discountPercentage && result?.discountPercentage != '0')? '%' : '$'
          const couponAmount = couponDiscountType == '%' ? result?.discountPercentage : result?.discountAmount
          const couponInfo = {
            amountDiscountedInDollars: Number(result?.discountAmount ?? 0),
            couponCode: result?.code,
            couponAmount,
            couponPercentage: result?.discountPercentage,
            couponDiscountType,
            isCannabisOnly: result?.isForCannabisOnly,
            couponName: result?.name,
            msoCouponCode: result?.msoCoupon?.code,
            discountId: result?._id?.toString(),
            supplier: result?.supplier,
            group: result?.group,
            brand: result?.brand,
            product: result?.product,
            size: result?.size,
            minimumQuantity: result?.minimumQuantity,
            maximumQuantity: result?.maximumQuantity
          }

          const alpineDiscount = { ...discount, couponId: result._id.toString() }
          const coupon = { alpineDiscount, ...result, type: 'alpine-discount', ...couponInfo };
          try {
              const order = await this.applyDiscountToOrder({
              discount: coupon,
              orderId: this.posOrder._id,
              order: this.posOrder,
            })
            this.setSelectedDiscounts(order)
            this.isBlurred = false;
          }
          catch (error) {
            this.isBlurred = false;
          }
        },
        negativeCallback: () => {
          this.isBlurred = false;
        }
      });
    },
    close() {
      this.setDiscountsPopUp(false);
    },
    async removeDiscount(discount) {
      this.isBlurred = true;

      // Engine mode: remove by DiscountRule ID (posDiscountID)
      if (this.useDiscountRulesEngine) {
        this.removeDiscountFromOrder({
          discountId: discount.posDiscountID,
          orderId: this.posOrder._id,
          order: this.posOrder,
          positiveCallback: (order) => {
            this.setSelectedDiscounts(order);
            this.isBlurred = false;
          },
          negativeCallback: () => {
            this.isBlurred = false;
          },
        });
        return;
      }

      // Legacy mode: find the discount in discountsApplied array
      const disc = this.posOrder.discountsApplied.find(disc => discount.id === disc.id || discount.posDiscountID === disc.alpineDiscount?.couponId);
      this.removeDiscountFromOrder({
        discountId: disc._id,
        orderId: this.posOrder._id,
        order: this.posOrder,
        positiveCallback: (order) => {
          this.setSelectedDiscounts(order)
          this.isBlurred = false;
        },
        negativeCallback: (order) => {
          this.isBlurred = false;
        },
      });
    },
    setSelectedDiscounts(order) {
      this.selectedDiscounts = order?.discountsApplied ?? [];
    },
    validateAlpineDiscountsApplied(discount) {
      const alpineDiscountsApplied = this.selectedDiscounts?.filter(d => d.type == 'alpine-discount')?.map(d => d.alpineDiscount) ?? []
      const totalAdded = alpineDiscountsApplied.reduce((acc, curr) => acc + (curr?.pointsDeduction ?? 0), 0)
      const pointsDeduction = discount.pointsDeduction ?? 0

      const disableStacking = discount.disableStacking == true
      const discountsAlreadyApplied = alpineDiscountsApplied.length > 0
      const selectedDiscountsHaveStackingDisabled = alpineDiscountsApplied.filter(d => d.disableStacking === true).length > 0

      if (disableStacking && discountsAlreadyApplied) {
        this.setMessage({
          type: 'error',
          title: 'Error',
          text: 'Reward does not allow stacking.',
        });
        return false;
      }

      if (selectedDiscountsHaveStackingDisabled) {
        this.setMessage({
          type: 'error',
          title: 'Error',
          text: 'Applied Rewards do not allow stacking.',
        });
        return false;
      }

      if ((totalAdded + pointsDeduction) > this.pointBalance) {
        this.setMessage({
          type: 'error',
          title: 'Error',
          text: `Cannot add reward as it will exceed customer point balance ${this.pointBalance} < ${totalAdded + pointsDeduction}`,
        });
        return false;
      }
      return true;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "./_loyaltyDiscounts.scss";
</style>
